/**
 * The name an edited copy of a file gets.
 *
 * The editor always writes JPEG, so the extension changes with the bytes — the old flow kept the
 * original name on a re-encoded file, which made `photo.png` a lie.
 *
 * It has to be a **new** name rather than the original one, because saving an edit uploads before it
 * deletes (a failed delete leaves two copies, a failed upload after a delete has lost the photo).
 * Two files therefore exist for a moment, and the upload endpoint prefixes a colliding name with
 * eight random hex characters — so a name that is already taken is worse than a suffix. Editing the
 * same photo twice gives `-edited-2`, not `3f2a9b1cphoto-edited.jpg`.
 */
const EDITED = "-edited";

export const editedFilename = (
  original: string,
  taken: readonly string[],
): string => {
  const withoutExtension = original.replace(/\.[^./\\]+$/, "");
  // Strip a suffix this function added earlier, so a third edit is `-edited-3` rather than
  // `-edited-2-edited`.
  const base = withoutExtension.replace(new RegExp(`${EDITED}(-\\d+)?$`), "");

  const candidate = (attempt: number) =>
    `${base}${EDITED}${attempt > 1 ? `-${attempt}` : ""}.jpg`;

  let attempt = 1;
  while (taken.includes(candidate(attempt))) attempt += 1;
  return candidate(attempt);
};
